import React, {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  RefObject,
  useRef
} from 'react'
// eslint-disable-next-line no-restricted-imports
import {css, cx} from '@emotion/css'
import noop from 'lodash/noop'

import {Button} from '../Button'
import {Icon} from '../Icon'
import {NavBar, NavBarProps, NavBarTitle} from '../NavBar'
import {ANIMATION_TYPES, AnimationType, Overlay} from '../Overlay'
import useBodyScrollLock from '../Overlay/useBodyScrollLock'
import {Header, OverlayBody, OverlayContent, OverlayFooter} from './styles'

interface Props {
  /** Whether the overlay is open */
  isOpen: boolean
  /** The body of the overlay */
  children: ReactNode
  /** The callback for closing the overlay  */
  onClose?: () => void
  /** Whether to show the overlay close button */
  showCloseButton?: boolean
  /** Toggle to show or hide the header box shadow */
  hasHeaderBoxShadow?: boolean
  /** The style of animation for opening and closing */
  animationType?: AnimationType
  /** Whether to add a shade to the background */
  hasShadeBackground?: boolean
  /** Callback when user clicks on the background shadow */
  onShadeBackgroundClick?: () => void
  /** Props forwarded to the NavBar rendered at the top of the overlay. */
  navBarProps?: Omit<NavBarProps, 'isCompact' | 'bottomContent'>
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Render children in a hidden div outside the portal so crawlers can index the content without JS */
  prerenderContent?: boolean
}

interface SimpleComponentProps {
  children: ReactNode
  className?: string
}

export const OverlayPageContent = forwardRef(
  (
    {children, className}: SimpleComponentProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) => {
    const localRef = useRef<HTMLDivElement>()
    const ref = (forwardedRef || localRef) as RefObject<HTMLDivElement> // Ref is optional but required from now on

    useBodyScrollLock({ref})

    return (
      <OverlayContent ref={ref} className={className}>
        {children}
      </OverlayContent>
    )
  }
)

OverlayPageContent.displayName = 'OverlayPageContent'

export const OverlayPageFooter = ({
  children,
  className
}: PropsWithChildren<{
  className?: string
}>) => <OverlayFooter className={className}>{children}</OverlayFooter>

const shadeBackgroundStyles = css`
  height: auto;
`

export const OverlayPage = ({
  isOpen,
  children,
  onClose,
  showCloseButton = true,
  hasHeaderBoxShadow = true,
  animationType = ANIMATION_TYPES.SLIDE_DOWN,
  hasShadeBackground = false,
  onShadeBackgroundClick = noop,
  navBarProps = {},
  className,
  dataId,
  prerenderContent
}: Props) => {
  const {
    centerContent,
    endContent: navBarEndContent,
    withDivider,
    variant,
    floating,
    centerAligned = true,
    ...navBarRest
  } = navBarProps

  const closeButton = (
    <Button
      dataId="OverlayPageCloseButton"
      variant={variant === 'inverse' ? 'ghostInverse' : 'ghost'}
      size="lg"
      iconEnd={
        <Icon
          name="Close"
          colorPath={
            variant === 'inverse'
              ? 'content.neutral.inverse'
              : 'content.neutral.primary'
          }
        />
      }
      onClick={onClose}
    />
  ) as ReactElement

  const headerEndContent =
    navBarEndContent ?? (showCloseButton ? closeButton : undefined)

  return (
    <Overlay
      prerenderContent={prerenderContent}
      shadeBackground={hasShadeBackground}
      animationType={animationType}
      isOpen={isOpen}
      onShadeBackgroundClick={onShadeBackgroundClick}
      enableBodyScrollLock={false}
      customStyles={hasShadeBackground ? shadeBackgroundStyles : undefined}
      onClose={onClose}
      className={className}
      dataId={dataId}
    >
      <OverlayBody>
        <Header
          data-id="NavBar"
          className={cx(hasHeaderBoxShadow && '--shadow')}
        >
          <NavBar
            {...navBarRest}
            isCompact
            variant={variant}
            floating={floating}
            centerAligned={centerAligned}
            withDivider={Boolean(withDivider)}
            centerContent={
              centerContent && <NavBarTitle>{centerContent}</NavBarTitle>
            }
            endContent={headerEndContent}
          />
        </Header>
        {children}
      </OverlayBody>
    </Overlay>
  )
}
